import { useState, useEffect } from "react";
import Select, { components } from "react-select";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { Loader2 } from "lucide-react"; // spinner icon
import { getPartnersList } from "../../services/merchant";
import { fetchPartnerByName } from "../../services/partner";
import { getCategories } from "../../services/caregory";
import { calculateSchemes, EmiCalcRequest } from "../../services/scheme";
import axios from "axios";

export default function Calculator() {
    const [vehicleType, setVehicleType] = useState("");
    const [approvedLimit, setApprovedLimit] = useState("");
    const [grossTenure, setGrossTenure] = useState("");
    const [lender, setLender] = useState("");
    const [showRates, setShowRates] = useState(false);
    const [isLoadingSchemes, setIsLoadingSchemes] = useState(false);
    const [schemesData, setSchemesData] = useState<any[]>([]);
    const [brandZypeVersion, setBrandZypeVersion] = useState("v1");


    const merchantData = useSelector((state: any) => state.merchant);
    const partner = merchantData?.merchant?.afpId;
    const zypeVersion = merchantData?.merchant?.afpDetails?.zypeVersion ?? "v1";

    const [brands, setBrands] = useState<any[]>([]);
    const [selectedBrand, setSelectedBrand] = useState<any>(null);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);

    const [brandOptions, setBrandOptions] = useState<any[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
    const [subCategoryOptions, setSubCategoryOptions] = useState<any[]>([]);
    const [isLoadingBrands, setIsLoadingBrands] = useState(false);
    const [partnerSubCats, setPartnerSubCats] = useState<string[]>([]);

    // ✅ Fetch brands
    useEffect(() => {
        const loadBrands = async () => {
            try {
                setIsLoadingBrands(true);

                const names = await getPartnersList(); // ✅ directly an array of strings

                if (Array.isArray(names) && names.length > 0) {
                    setBrandOptions(
                        names.map((name: string) => ({
                            label: name,
                            value: name
                        }))
                    );
                } else {
                    setBrandOptions([]);
                }
            } catch (err) {
                console.error("Error fetching partner list:", err);
                setBrandOptions([]);
            } finally {
                setIsLoadingBrands(false);
            }
        };

        loadBrands();
    }, []);



    // ✅ Handle categories & subcategories when brand changes
    useEffect(() => {
        // Reset everything
        setCategoryOptions([]);
        setSelectedCategory(null);
        setSubCategoryOptions([]);
        setSelectedSubCategory(null);

        if (!selectedBrand) return;

        const loadPartnerDetails = async () => {
            try {
                const token = localStorage.getItem("localToken");
                const partnerDoc = await fetchPartnerByName(selectedBrand.value, token);
                setBrandZypeVersion(partnerDoc?.zypeVersion || "v1");


                // Store categories from fetched partner
                const categories = partnerDoc?.category || [];

                // Set category dropdown
                const catOpts = categories.map((c: string) => ({ label: c, value: c }));
                setCategoryOptions(catOpts);

                // Auto-select if only 1
                if (catOpts.length === 1) setSelectedCategory(catOpts[0].value);

                // Save partner subcats for next step
                setPartnerSubCats(partnerDoc?.subCategory || []);
            } catch (err) {
                console.error("Error fetching partner details:", err);
            }
        };

        loadPartnerDetails();
    }, [selectedBrand]);


    // ✅ Handle subcategories when category is selected
    useEffect(() => {
        if (!selectedCategory || !selectedBrand) {
            setSubCategoryOptions([]);
            setSelectedSubCategory(null);
            return;
        }

        const loadCategorySubs = async () => {
            try {
                const token = localStorage.getItem("localToken");

                // ✅ Use existing getCategories service
                const res = await getCategories({
                    categoryId: selectedCategory,
                });

                // ✅ Access category object
                const category = res.data?.[0];

                const categorySubs = category?.subCategory || [];

                // ✅ Filter by partner-supported subcategories
                const matched = categorySubs.filter((sub: string) =>
                    partnerSubCats.includes(sub)
                );

                if (matched.length > 1) {
                    setSubCategoryOptions(
                        matched.map((s: string) => ({ label: s, value: s }))
                    );
                    setSelectedSubCategory(null);
                } else if (matched.length === 1) {
                    setSubCategoryOptions([{ label: matched[0], value: matched[0] }]);
                    setSelectedSubCategory(matched[0]);
                } else {
                    setSubCategoryOptions([]);
                    setSelectedSubCategory(null);
                }
            } catch (err) {
                console.error("Error fetching category subcategories:", err);
            }
        };

        loadCategorySubs();
    }, [selectedCategory, selectedBrand]);


    // ✅ Fetch Schemes API
    useEffect(() => {
        const fetchSchemes = async () => {
            if (!approvedLimit || !lender || !selectedBrand) return;

            setIsLoadingSchemes(true);
            setGrossTenure("");
            setSchemesData([]);

            try {
                // ✅ lender mapping same as before
                const mappedLender =
                    lender.toLowerCase() === "respo"
                        ? "zype"
                        : lender.toLowerCase() === "bhanix"
                            ? "cashe"
                            : lender.toLowerCase();

                // ✅ Build request payload according to EmiCalcRequest interface
                const payload: EmiCalcRequest = {
                    amount: Number(approvedLimit.replace(/,/g, "")),
                    partner: selectedBrand.label,
                    lender: mappedLender,
                    zypeVersion: brandZypeVersion,
                };

                if (selectedCategory) payload.category = selectedCategory;
                if (selectedSubCategory) payload.subCategory = selectedSubCategory;

                console.log("✅ PAYLOAD SENT TO calculateSchemes:", payload);

                // ✅ Call your service function
                const data = await calculateSchemes(payload);

                setSchemesData(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching schemes:", err);
                setSchemesData([]);
            } finally {
                setIsLoadingSchemes(false);
            }
        };

        fetchSchemes();
    }, [
        approvedLimit,
        lender,
        selectedBrand,
        selectedCategory,
        selectedSubCategory,
        brandZypeVersion,
    ]);


    const isCheckRatesDisabled =
        !approvedLimit.replace(/,/g, "") || !lender || !grossTenure ||
        (categoryOptions.length > 0 && !selectedCategory) ||
        (subCategoryOptions.length > 0 && !selectedSubCategory);


    const vehicleOptions = [
        { label: "Low Speed Vehicle", value: "Low Speed Vehicle" },
        { label: "High Speed Vehicle", value: "High Speed Vehicle" },
    ];

    // Dynamically populated tenure options
    const tenureOptions =
        schemesData?.map((item) => ({
            label: item.scheme?.name,
            value: item.scheme?.name,
        })) || [];

    const COLORS = ["#c7c7c7", "#4f5df4"];

    // Custom Select Styles
    const customSelectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            borderColor: state.isFocused ? "#6366f1" : "#d1d5db",
            boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "none",
            "&:hover": { borderColor: "#6366f1" },
            fontWeight: 600,
            height: "44px",
            minHeight: "44px",
        }),
        placeholder: (provided: any) => ({
            ...provided,
            fontWeight: "normal",
            color: "#9ca3af",
        }),
        singleValue: (provided: any) => ({
            ...provided,
            fontWeight: "600",
        }),
    };






    // Get selected scheme object
    const selectedScheme = schemesData.find(
        (item) => item.scheme?.name === grossTenure
    );

    const handleCheckRates = () => {
        if (!selectedScheme) return;
        setShowRates(true);
    };

    const calc = selectedScheme?.calculations || {};
    const resetShowRates = () => setShowRates(false);


    return (
        <div className="h-screen lg:h-screen flex flex-col items-center p-6 bg-white 
                overflow-auto lg:overflow-hidden">
            <div className="w-full max-w-3xl space-y-6">

                {/* 🔹 Row 1: Brand, Category, SubCategory */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Brand */}
                    {(
                        <div className="flex flex-col">
                            <label className="text-gray-700 mb-2 text-left">Select Brand</label>

                            <Select
                                styles={{
                                    ...customSelectStyles,
                                    placeholder: (provided) => ({
                                        ...provided,
                                        textAlign: "left",
                                        fontWeight: "normal",
                                        color: "#9ca3af",
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        fontWeight: "600",
                                    }),
                                }}
                                placeholder="Select Brand"
                                className="text-left"
                                isLoading={isLoadingBrands}
                                options={brandOptions}  // ✅ always mapped array
                                value={selectedBrand}
                                onChange={(option) => {
                                    setSelectedBrand(option);
                                    setSelectedCategory(null);
                                    setSelectedSubCategory(null);
                                    resetShowRates();
                                }}
                            />
                        </div>
                    )}


                    {/* Category */}
                    {selectedBrand && categoryOptions.length > 1 && (
                        <div className="flex flex-col">
                            <label className="text-gray-700 mb-2 text-left">Select Category</label>
                            <Select
                                styles={{
                                    ...customSelectStyles,
                                    placeholder: (provided) => ({
                                        ...provided,
                                        textAlign: "left",
                                        fontWeight: "normal",
                                        color: "#9ca3af",
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        fontWeight: "600",
                                    }),
                                }}
                                placeholder="Select Category"
                                className="text-left"
                                options={categoryOptions}
                                value={
                                    selectedCategory
                                        ? categoryOptions.find((c) => c.value === selectedCategory)
                                        : null
                                }
                                onChange={(option) => {
                                    setSelectedCategory(option?.value || "");
                                    setSelectedSubCategory("");
                                    resetShowRates();
                                }}
                            />
                        </div>
                    )}

                    {/* SubCategory */}
                    {selectedCategory && subCategoryOptions.length > 1 && (
                        <div className="flex flex-col">
                            <label className="text-gray-700 mb-2 text-left">Select Sub Category</label>
                            <Select
                                styles={{
                                    ...customSelectStyles,
                                    placeholder: (provided) => ({
                                        ...provided,
                                        textAlign: "left",
                                        fontWeight: "normal",
                                        color: "#9ca3af",
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        fontWeight: "600",
                                    }),
                                }}
                                placeholder="Select Sub Category"
                                className="text-left"
                                options={subCategoryOptions}
                                value={
                                    selectedSubCategory
                                        ? subCategoryOptions.find(
                                            (s) => s.value === selectedSubCategory
                                        )
                                        : null
                                }
                                onChange={(option) => {
                                    setSelectedSubCategory(option?.value || "");
                                    resetShowRates();
                                }}

                            />
                        </div>
                    )}
                </div>

                {/* 🔹 Row 2: Approved Limit, Lender, Gross Tenure */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Approved Limit */}
                    <div className="flex flex-col">
                        <label className="text-gray-700 mb-2 text-left">Approved Limit</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-700 font-semibold">
                                ₹
                            </span>
                            <input
                                type="text"
                                value={approvedLimit}
                                onChange={(e) => {
                                    const formatted = e.target.value.replace(/[^0-9,]/g, "");
                                    setApprovedLimit(formatted);
                                    resetShowRates();
                                }}
                                placeholder="Enter amount"
                                className="w-full border border-gray-300 rounded-md pl-8 pr-4 font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-left placeholder:font-normal placeholder:text-gray-400 h-[44px]"
                                inputMode="numeric"
                            />
                        </div>
                    </div>

                    {/* Lender */}
                    <div className="flex flex-col">
                        <label className="text-gray-700 mb-2 text-left">Lender</label>
                        <Select
                            styles={customSelectStyles}
                            placeholder="Select Lender"
                            className="text-left"
                            options={[
                                { label: "RESPO", value: "RESPO" },
                                { label: "BHANIX", value: "BHANIX" },
                            ]}
                            value={
                                lender
                                    ? [
                                        { label: "RESPO", value: "RESPO" },
                                        { label: "BHANIX", value: "BHANIX" },
                                    ].find((l) => l.value === lender)
                                    : null
                            }
                            onChange={(e) => {
                                setLender(e?.value || "");
                                resetShowRates();
                            }}

                        />
                    </div>

                    {/* Gross Tenure */}
                    <div className="flex flex-col">
                        <label className="text-gray-700 mb-2 text-left">Gross Tenure</label>
                        <Select
                            styles={{
                                ...customSelectStyles,
                                control: (base: any, state: any) => ({
                                    ...base,
                                    borderColor: state.isFocused ? "#6366f1" : "#d1d5db",
                                    boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "none",
                                    "&:hover": { borderColor: "#6366f1" },
                                    fontWeight: 600,
                                    height: "44px",
                                    minHeight: "44px",
                                    backgroundColor: "white",
                                    cursor: "pointer",
                                }),
                                indicatorsContainer: (base: any) => ({
                                    ...base,
                                    position: "relative",
                                }),
                            }}
                            components={{
                                DropdownIndicator: (props) =>
                                    isLoadingSchemes ? (
                                        <div className="pr-3">
                                            <Loader2
                                                className="animate-spin text-indigo-600"
                                                size={18}
                                            />
                                        </div>
                                    ) : (
                                        <components.DropdownIndicator {...props} />
                                    ),
                            }}
                            placeholder="Select Scheme Tenure"
                            className="text-left"
                            options={tenureOptions}
                            value={
                                grossTenure
                                    ? tenureOptions.find((t) => t.value === grossTenure)
                                    : null
                            }
                            onChange={(e) => {
                                setGrossTenure(e?.value || "");
                                resetShowRates();
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* ✅ Check Rates Button (always visible once selections are valid) */}
            <div className="flex justify-center items-center mt-6">
                <button
                    onClick={handleCheckRates}
                    className={`py-3 px-16 rounded-full font-medium transition-all w-[320px] ${!isCheckRatesDisabled
                            ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    disabled={isCheckRatesDisabled}
                >
                    Check Rates
                </button>
            </div>

            {/* 🔹 Buttons + Rate Details Section */}
            {showRates && selectedScheme ? (
                <motion.div
                    className="mt-4 space-y-6"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    {/* ✅ Text (left) + Button (right) on the same line */}
                    {/* <div className="flex justify-center items-center">


                        <button
                            onClick={handleCheckRates}
                            className={`py-3 px-16 rounded-full font-medium transition-all w-[320px] ${!isCheckRatesDisabled
                                ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                                : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                }`}
                            disabled={isCheckRatesDisabled}
                        >
                            Check Rates
                        </button>

                    </div> */}

                    {/* 💡 The grid section you already have */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                        {/* Left Side - EMI Details */}
                        <div className="text-gray-900 space-y-3">
                            <div className="flex justify-between items-start">
                                <p className="font-medium">Monthly EMI</p>
                                <p className="font-semibold text-gray-900 underline">
                                    ₹{calc?.monthlyEMI?.toLocaleString() || "--"}
                                </p>
                            </div>

                            <div className="flex justify-between items-start">
                                <p className="font-medium">Interest Amount</p>
                                <p className="font-semibold text-gray-900">
                                    ₹{calc?.totalInterest?.toLocaleString() || "0"}
                                </p>
                            </div>

                            <div className="flex justify-between items-start">
                                <p className="font-medium">Advance EMI</p>
                                <p className="font-semibold text-gray-900">
                                    ₹{calc?.advancedEmiPayment?.toLocaleString() || "0"}
                                </p>
                            </div>

                            <div className="flex justify-between items-start">
                                <p className="font-medium">Processing Fee</p>
                                <p className="font-semibold text-gray-900">
                                    ₹{calc?.totalProcessingFee?.toLocaleString() || "--"}
                                </p>
                            </div>

                            <div className="flex justify-between items-start mt-3 gap-8">
                                <p className="font-medium">Total Down Payment</p>
                                <p className="font-semibold text-gray-900 underline">
                                    ₹{calc?.totalDownPayment?.toLocaleString() || "--"}
                                </p>
                            </div>

                            <div className="flex justify-between items-start gap-10">
                                <p className="font-medium">Disbursement to Dealer</p>
                                <p className="font-semibold text-gray-900 underline">
                                    ₹{calc?.DBDToDealer?.toLocaleString() || "--"}
                                </p>
                            </div>
                        </div>

                        {/* Right Side - Chart Card */}
                        <div className="relative">
                            <div className="bg-gray-50 border rounded-3xl shadow-sm flex flex-col items-center px-6 py-8 w-full max-w-xl">
                                <div className="flex w-full justify-between items-center gap-4">
                                    {/* Left - Two Indigo Cards */}
                                    <div className="flex flex-col space-y-4">
                                        <div className="bg-indigo-600 text-white px-5 py-3 rounded-xl text-sm font-semibold text-center shadow-sm w-36">
                                            <p className="text-xs font-normal">Monthly EMI</p>
                                            <p className="text-lg font-bold">
                                                ₹{calc?.monthlyEMI?.toLocaleString() || "--"}
                                            </p>
                                        </div>

                                        <div className="bg-indigo-600 text-white px-5 py-3 rounded-xl text-sm font-semibold text-center shadow-sm w-36">
                                            <p className="text-[11px] font-normal whitespace-nowrap">
                                                Total Down Payment
                                            </p>
                                            <p className="text-lg font-bold">
                                                ₹{calc?.totalDownPayment?.toLocaleString() || "--"}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Right - Circular Chart */}
                                    <div className="relative flex justify-center items-center flex-shrink-0">
                                        <div className="w-[170px] h-[170px] bg-white rounded-full shadow-md flex justify-center items-center">
                                            <ResponsiveContainer width={140} height={140}>
                                                <PieChart>
                                                    <Pie
                                                        data={[
                                                            { name: "Principal Amount", value: calc?.loanAmount || 0 },
                                                            { name: "Interest", value: calc?.totalInterest || 0 },
                                                        ]}
                                                        dataKey="value"
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={50}
                                                        outerRadius={65}
                                                        paddingAngle={5}
                                                        stroke="none"
                                                    >
                                                        <Cell fill="#c7c7c7" />
                                                        <Cell fill="#4f5df4" />
                                                    </Pie>
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>

                                {/* Legend below */}
                                <div className="flex justify-center w-full mt-6 text-sm space-x-10">
                                    <div className="flex items-start space-x-3">
                                        <div className="w-7 h-7 rounded-full bg-gray-400 mt-1"></div>
                                        <div className="text-gray-800 font-semibold leading-tight text-left">
                                            ₹{calc?.loanAmount?.toLocaleString() || "--"} <br />
                                            <span className="text-xs font-normal text-gray-500">
                                                Principal Amount
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-3">
                                        <div className="w-7 h-7 rounded-full bg-indigo-600 mt-1"></div>
                                        <div className="text-gray-800 font-semibold leading-tight text-left">
                                            ₹{calc?.totalInterest?.toLocaleString() || "--"} <br />
                                            <span className="text-xs font-normal text-gray-500">
                                                Interest Amount
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            ) : null}


        </div>
    );

}
